import { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight } from '../../../icons';
import type { EmployeeRowDto } from '../../../api/hr-employee';
import type { ContractExpiryItem, InputCompletionItem } from './employee-hr-types';
import { NOT_READY_TEXT } from './employee-hr-types';
import { PROBATION_END_LABEL, probationDefaultNote } from './employees-shared';
import './employee-notices.css';

/**
 * 직원 관리 — 표 위 «할 일» 알림 줄 (2026-10-18 · web 레인 W10-25 · 렌즈 Toss · 사용자 결정 B안)
 *
 * 카드 넷(`EmployeeKpis`)을 걷고 할 일 둘만 한 줄씩 남긴다. 현황 숫자(재직 추이 등)는 인사 통계(26)로 옮긴다
 * (사용자 결정 2026-10-18 · design 레인 시안 뒤 W10-26). `EmployeeKpis.tsx` 는 지우지 않는다 — 그 셈을 26 에서 다시 쓴다.
 *   ① 인사정보 빈칸 — 빈 «칸» 수와 많이 빈 순 항목. ★ 사람 수는 응답에 없다(한 사람이 두 칸이면 갈린다 · 짝 문서 25-3).
 *   ② 30일 안에 끝나는 수습 · 전환일 지남 — «목록 보기»는 카드가 보이던 이름 목록을 **그 자리에서** 펼친다.
 * 0 이면 그 줄을 그리지 않는다(틀-6 · 0 늘어놓기).
 */
export interface EmployeeNoticesProps {
  rows: readonly EmployeeRowDto[];
  missingHireDate: number;
  inputCompletion?: { total: number; items: ReadonlyArray<InputCompletionItem> } | null;
  probation?: { inProbation: number; endingWithin30Days: number; overdue?: number } | null;
  probationMonths?: number | null;
  probationMonthsSource?: 'DEFAULT' | 'INSTITUTION' | null;
  expiries?: readonly ContractExpiryItem[] | null;
  /** 입사일 빈칸이 있을 때 «입사일 없는 직원 보기» — 이 화면의 거르기를 켠다(일괄 채우기 화면은 아직 없다). */
  onShowNoHire?: () => void;
}

interface Blank { key: string; label: string; missing: number }

/** 빈 칸 — 서버 집계(아홉 칸)가 있으면 그 값, 옛 서버면 행에서 셀 수 있는 네 칸(`EmployeeKpis` 와 같은 셈). */
export function blanksOf(
  rows: readonly EmployeeRowDto[], missingHireDate: number,
  inputCompletion: EmployeeNoticesProps['inputCompletion'],
): Blank[] {
  const items: Blank[] = inputCompletion
    ? inputCompletion.items.map((m) => ({ key: m.key, label: m.label, missing: m.missing }))
    : [
      { key: 'hireDate', label: '입사일', missing: missingHireDate },
      { key: 'employeeNumber', label: '사번', missing: rows.filter((r) => !r.employeeNumber).length },
      { key: 'team', label: '소속 팀', missing: rows.filter((r) => !r.teamId).length },
      { key: 'employmentType', label: '고용형태', missing: rows.filter((r) => !r.employmentType).length },
    ];
  return items.filter((m) => m.missing > 0).sort((a, b) => b.missing - a.missing);
}

export function EmployeeNotices({
  rows, missingHireDate, inputCompletion = null, probation = null,
  probationMonths = null, probationMonthsSource = null, expiries = null, onShowNoHire,
}: EmployeeNoticesProps) {
  const [open, setOpen] = useState(false);
  const blanks = blanksOf(rows, missingHireDate, inputCompletion);
  const cells = blanks.reduce((s, m) => s + m.missing, 0);
  const hireBlank = blanks.find((m) => m.key === 'hireDate')?.missing ?? 0;
  const showProbation = !!probation && (probation.endingWithin30Days > 0 || (probation.overdue ?? 0) > 0);
  if (cells === 0 && !showProbation) return null;

  return (
    <section className="hre-notices" aria-label="할 일 알림">
      {cells > 0 && (
        <p className="hre-notice" role="note">
          <AlertCircle aria-hidden />
          <span><b>인사정보 빈칸 {cells}칸</b> — {blanks.map((m) => `${m.label} ${m.missing}`).join(' · ')}</span>
          {hireBlank > 0 && onShowNoHire && (
            <button type="button" className="hre-notice__act" onClick={onShowNoHire}>
              입사일 없는 직원 보기<ChevronRight aria-hidden />
            </button>
          )}
        </p>
      )}
      {showProbation && probation && (
        <div className="hre-notice-group">
          <p className="hre-notice" role="note">
            <AlertCircle aria-hidden />
            <span>
              <b>30일 안에 끝나는 수습 {probation.endingWithin30Days}명</b>
              {probation.overdue !== undefined && <> · 전환일 지남 {probation.overdue}명</>}
            </span>
            <button
              type="button" className="hre-notice__act" aria-expanded={open} aria-controls="hre-probation-list"
              onClick={() => setOpen((v) => !v)}
            >
              목록 보기<ChevronDown aria-hidden />
            </button>
          </p>
          {open && (
            <div id="hre-probation-list" className="hre-notice__more">
              {expiries && expiries.length > 0 ? (
                <ul className="hre-expiry" aria-label={PROBATION_END_LABEL}>
                  {expiries.map((e) => (
                    <li key={`${e.userId}-${e.kind}`}><span>{e.name} · {e.kind === 'CONTRACT' ? '계약 만료' : PROBATION_END_LABEL}</span><span className="num">{e.endsOn}</span></li>
                  ))}
                </ul>
              ) : <p className="hre-kpi-note">30일 안에 끝나는 수습이 없습니다</p>}
              <p className="hre-kpi-note">
                계약 종료일 — {NOT_READY_TEXT}
                {probationMonthsSource === 'DEFAULT' && <> · 수습 기간은 {probationDefaultNote(probationMonths ?? 3)}</>}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
